import last from "lodash/last";
import omit from "lodash/omit";
import _uniqueId from "lodash/uniqueId";
import {
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_ID_KEY,
  FIELD_UNIQUE_ID_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import {
  ActivityFormType,
  MultiActivityForm,
  RoleFormKey,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { useFieldArray, useFormContext } from "react-hook-form";

import { useInitialRoleData } from "./use_initial_role_data";

export const getRolesFormKey = (subtemplateIndex: number): RolesFormKey =>
  `${FORM_SUBTEMPLATES_KEY}.${subtemplateIndex}.${FORM_ROLES_KEY}` as const;

type UseRolesFieldArray = {
  rolesFormKey: RolesFormKey;
  fieldsBlacklist?: string[];
  removeSubtemplate: () => void;
};

export const useRolesFieldArray = ({
  rolesFormKey,
  fieldsBlacklist,
  removeSubtemplate
}: UseRolesFieldArray) => {
  const { control, getValues, setValue } = useFormContext<MultiActivityForm>();
  const { getInitialRoleData } = useInitialRoleData({ fieldsBlacklist });
  const { setRoleData, clearRoleData } = useMultiActivityFormStoreActions();

  const { fields: rolesFields, append } = useFieldArray<MultiActivityForm, RolesFormKey>({
    name: rolesFormKey,
    control
  });

  const addRole = (subtemplateKey: string) => {
    const uniqueId = _uniqueId();
    append(getInitialRoleData({ subtemplateKey, isNew: true, uniqueId }));
    setRoleData(uniqueId);
  };

  const duplicateRole = (roleFormKey: RoleFormKey) => {
    const uniqueId = _uniqueId();
    const values = omit(
      // An attachment should not be shared between roles
      {
        ...getValues(roleFormKey),
        [FIELD_ATTACHMENT_IDS_KEY]: [],
        [FIELD_UNIQUE_ID_KEY]: uniqueId
      },
      [...(fieldsBlacklist || []), FIELD_ID_KEY]
    );
    append(values as ActivityFormType);
    setRoleData(uniqueId);
  };

  const removeRole = (roleFormKey: RoleFormKey) => {
    // TODO: [SP-7599] investigate why remove from useFieldArray does not work,
    // for now using `setValue`
    const roleIndex = Number(last(roleFormKey.split(".")));
    const { uniqueId } = getValues(roleFormKey);
    const allRoles = getValues(rolesFormKey);
    const updatedRoles = allRoles.filter((_, i) => i !== roleIndex);
    setValue(rolesFormKey, updatedRoles, { shouldDirty: true });
    if (updatedRoles.length === 0) {
      removeSubtemplate();
    }
    clearRoleData(uniqueId);
  };

  return {
    rolesFields,
    addRole,
    removeRole,
    duplicateRole
  };
};
